import { useCanvasStore } from './canvas-store'
import type { Rect, Transform } from './geometry'

// Soft, interruptible pan/zoom animation. We tween the store's scale/offset (not a CSS
// transform) so the dot grid — a separate canvas that reads scale/offset directly — stays
// in sync with the cards. Cards are passed to <Canvas> as children, so they don't re-render
// per frame; only the canvas + grid do.

let rafId: number | null = null

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

export function animateViewTo(target: Transform, durationMs = 350) {
  const { scale, offset } = useCanvasStore.getState()
  const start = { scale, offset: { ...offset } }
  if (rafId !== null) cancelAnimationFrame(rafId)
  const t0 = performance.now()
  const step = (now: number) => {
    const p = Math.min(1, (now - t0) / durationMs)
    const e = easeOutCubic(p)
    useCanvasStore.getState().setTransform({
      scale: start.scale + (target.scale - start.scale) * e,
      offset: {
        x: start.offset.x + (target.offset.x - start.offset.x) * e,
        y: start.offset.y + (target.offset.y - start.offset.y) * e,
      },
    })
    rafId = p < 1 ? requestAnimationFrame(step) : null
  }
  rafId = requestAnimationFrame(step)
}

// Bring `rect` (world coords) into view by softly panning to center it — but only when it's
// not already comfortably visible, so adding a card that landed in view doesn't jump. Keeps
// the current zoom.
export function ensureRectVisible(rect: Rect, marginPx = 48) {
  const { scale, offset, viewport } = useCanvasStore.getState()
  if (viewport.width === 0 || viewport.height === 0) return

  const left = rect.x * scale + offset.x
  const top = rect.y * scale + offset.y
  const right = left + rect.w * scale
  const bottom = top + rect.h * scale
  const alreadyVisible =
    left >= marginPx &&
    top >= marginPx &&
    right <= viewport.width - marginPx &&
    bottom <= viewport.height - marginPx
  if (alreadyVisible) return

  const cx = rect.x + rect.w / 2
  const cy = rect.y + rect.h / 2
  animateViewTo({
    scale,
    offset: { x: viewport.width / 2 - cx * scale, y: viewport.height / 2 - cy * scale },
  })
}
